import { Button, Modal, notify } from '@affine/component';
import {
  AuthContent,
  AuthHeader,
  AuthInput,
} from '@affine/component/auth-components';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import {
  AuthService,
  DefaultServerService,
  ServersService,
} from '@affine/core/modules/cloud';
import type {
  DialogComponentProps,
  GLOBAL_DIALOG_SCHEMA,
} from '@affine/core/modules/dialogs';
import { Unreachable } from '@affine/env/constant';
import {
  authEmailDeliveryStatusQuery,
  AuthEmailDeliveryKind,
  sendChangeEmailWithStatusMutation,
  sendVerifyEmailWithStatusMutation,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';

type EmailSendState = 'idle' | 'sending' | 'pending' | 'sent';
const MAX_STATUS_READ_FAILURES = 3;

export const VerifyEmailDialog = ({
  close,
  server: serverBaseUrl,
  changeEmail,
}: DialogComponentProps<GLOBAL_DIALOG_SCHEMA['verify-email']>) => {
  const t = useI18n();
  const defaultServerService = useService(DefaultServerService);
  const serversService = useService(ServersService);
  let server;

  if (serverBaseUrl) {
    server = serversService.getServerByBaseUrl(serverBaseUrl);
    if (!server) {
      throw new Unreachable('Server not found');
    }
  } else {
    server = defaultServerService.server;
  }

  const authService = server.scope.get(AuthService);
  const account = useLiveData(authService.session.account$);
  const email = account?.email;
  const [sendState, setSendState] = useState<EmailSendState>('idle');
  const sendingRef = useRef(false);
  const passwordLimits = useLiveData(
    server.credentialsRequirement$.map(r => r?.password)
  );
  const serverName = useLiveData(server.config$.selector(c => c.serverName));
  const serverLogoUrl = useLiveData(server.config$.selector(c => c.logoUrl));

  const deliveryKind = changeEmail
    ? AuthEmailDeliveryKind.changeEmail
    : AuthEmailDeliveryKind.verifyEmail;
  const deliveryFailureTitle = changeEmail
    ? t['com.affine.auth.sent.change.email.fail']()
    : t['com.affine.auth.sent.verify.email.fail']();

  const reportDeliveryStatus = useCallback(
    (status: string) => {
      if (status === 'sent') {
        setSendState('sent');
        notify.success({
          title: t['com.affine.auth.sent.verify.email.hint'](),
        });
      } else if (status === 'failed') {
        setSendState('idle');
        notify.error({
          title: deliveryFailureTitle,
        });
      } else {
        setSendState('pending');
      }
    },
    [deliveryFailureTitle, t]
  );

  const reportStatusReadFailure = useCallback(() => {
    setSendState('idle');
    notify.error({
      title: t['com.affine.auth.toast.message.failed'](),
    });
  }, [t]);

  useEffect(() => {
    if (sendState !== 'pending') {
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    let statusReadFailures = 0;
    const poll = async () => {
      try {
        const result = await server.gql({
          query: authEmailDeliveryStatusQuery,
          variables: { kind: deliveryKind },
        });
        const status = result.authEmailDeliveryStatus.status;
        if (status !== 'pending') {
          reportDeliveryStatus(status);
          return;
        }
        statusReadFailures = 0;
      } catch {
        statusReadFailures += 1;
        if (statusReadFailures >= MAX_STATUS_READ_FAILURES) {
          reportStatusReadFailure();
          return;
        }
      }
      if (!cancelled) {
        timer = window.setTimeout(poll, 2_000);
      }
    };
    timer = window.setTimeout(poll, 2_000);
    return () => {
      cancelled = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [
    deliveryKind,
    reportDeliveryStatus,
    reportStatusReadFailure,
    sendState,
    server,
  ]);

  const onSendEmail = useAsyncCallback(async () => {
    // State updates are asynchronous, so use a ref to reject two rapid clicks.
    if (sendingRef.current || sendState !== 'idle') {
      return;
    }

    sendingRef.current = true;
    setSendState('sending');
    try {
      let status: string;
      if (changeEmail) {
        const result = await server.gql({
          query: sendChangeEmailWithStatusMutation,
          variables: {
            callbackUrl: `/auth/changeEmail`,
          },
        });
        status = result.sendChangeEmailWithStatus.status;
      } else {
        const result = await server.gql({
          query: sendVerifyEmailWithStatusMutation,
          variables: {
            callbackUrl: `/auth/verify-email`,
          },
        });
        status = result.sendVerifyEmailWithStatus.status;
      }

      reportDeliveryStatus(status);
    } catch (err) {
      console.error(err);
      notify.error({
        title: deliveryFailureTitle,
      });
    } finally {
      sendingRef.current = false;
      setSendState(current => (current === 'sending' ? 'idle' : current));
    }
  }, [
    changeEmail,
    deliveryFailureTitle,
    reportDeliveryStatus,
    sendState,
    server,
  ]);

  if (!email) {
    // should not happen
    throw new Unreachable();
  }

  if (!passwordLimits) {
    // should never reach here
    return null;
  }

  return (
    <Modal
      open
      onOpenChange={() => close()}
      width={400}
      minHeight={500}
      contentOptions={{
        ['data-testid' as string]: 'verify-email-modal',
        style: { padding: '44px 40px 20px' },
      }}
    >
      <AuthHeader
        title={serverName}
        logoUrl={serverLogoUrl}
        subTitle={
          changeEmail
            ? t['com.affine.settings.email.action.change']()
            : t['com.affine.settings.email.action.verify']()
        }
      />
      <AuthContent>
        <p>
          {changeEmail
            ? t['com.affine.auth.change.email.message']({ email })
            : t['com.affine.auth.verify.email.message']({ email })}
        </p>
        <AuthInput
          label={t['com.affine.settings.email']()}
          disabled={true}
          value={email}
        />
        <Button
          variant="primary"
          size="extraLarge"
          style={{ width: '100%' }}
          disabled={sendState !== 'idle'}
          loading={sendState === 'sending'}
          onClick={onSendEmail}
        >
          {sendState === 'sent'
            ? t['com.affine.auth.sent.verify.email.hint']()
            : sendState === 'sending' || sendState === 'pending'
              ? t['com.affine.auth.sending.verify.email']()
              : t['com.affine.auth.send.verify.email.hint']()}
        </Button>
      </AuthContent>
    </Modal>
  );
};
